import { SortableContext } from "@dnd-kit/sortable";
import { findIndex, move } from "ramda";
import { Component, type FC } from "react";
import { VisuallyHidden } from "react-aria";
import Select, { type GroupBase } from "react-select";

import { LoadingSpinner, TriggerButton, TriggerButtonSize } from "widgets";

import style from "../../../core/forms/styles/Sortable.module.scss";

import { SortableItem, SortableList } from "../../../core/forms/Sortable.js";
import { PopoverController } from "../../../core/overlays/components/OverlayControllers.js";
import { DEFAULT_COLORS } from "../../ColorSchemeConfigurationFields.js";
import { ColorSchemeConfigurationDialogForm } from "./ColorSchemeConfigurationDialogForm.js";
import { PhenotypeTermConfigDialogForm } from "./PhenotypeTermConfigDialogForm.js";
import type { ColorScheme, GenomicFeature } from "@/graphql.ts";
import type { IConfiguredPhenotype } from "@/redux/modules/genome-browser.ts";
import { isSamePhenotypeType } from "@/redux/modules/genome-browser.ts";
import { ORG_PROPERTY_VALUE_TYPE, PHENOTYPE_TYPE } from "@/settings/settings.ts";

export interface IPhenotypeSelectorProps {
  selectedPhenotypes: IConfiguredPhenotype[];
  availablePhenotypes: GenomicFeature[];
  phenotypesUpdated: (selectedPhenotypes: IConfiguredPhenotype[]) => void;
  isLoadingPhenotypes: boolean;
  dropdownLabel?: string;
}

class PhenotypeSelector extends Component<IPhenotypeSelectorProps> {
  constructor(props: IPhenotypeSelectorProps) {
    super(props);
  }

  state = {
    isOpen: false,
  };

  removeOption = (phenotype: IConfiguredPhenotype) => {
    const selectedPhenotypes: IConfiguredPhenotype[] = this.props.selectedPhenotypes.filter(
      (selectedPhenotype: IConfiguredPhenotype) => !isSamePhenotypeType(selectedPhenotype, phenotype),
    );
    this.props.phenotypesUpdated(selectedPhenotypes);
  };

  addOption = (phenotype: IConfiguredPhenotype) => {
    // define default configuration values for freshly selected numeric phenotypes
    const min: number = phenotype.valueType === ORG_PROPERTY_VALUE_TYPE.NUMERIC ? 0 : null;
    const max: number = phenotype.valueType === ORG_PROPERTY_VALUE_TYPE.NUMERIC ? 100 : null;
    const colorScheme: ColorScheme =
      phenotype.valueType === ORG_PROPERTY_VALUE_TYPE.NUMERIC ? { colors: DEFAULT_COLORS } : null;

    const selectedPhenotypes: IConfiguredPhenotype[] = this.props.selectedPhenotypes.slice(0);
    selectedPhenotypes.push({ ...phenotype, minValue: min, maxValue: max, colorScheme: colorScheme });
    this.props.phenotypesUpdated(selectedPhenotypes);
  };

  updateOption = (phenotype: IConfiguredPhenotype) => {
    const selectedPhenotypes: IConfiguredPhenotype[] = this.props.selectedPhenotypes.map((selectedPhenotype) =>
      isSamePhenotypeType(selectedPhenotype, phenotype) ? phenotype : selectedPhenotype,
    );
    this.props.phenotypesUpdated(selectedPhenotypes);
  };

  static getPhenotypeLabel = (phenotype: GenomicFeature) => {
    return `${phenotype.name}${phenotype.groupName ? ` (${phenotype.groupName})` : ""}`;
  };

  static getPhenotypeId = (phenotype: GenomicFeature) => {
    return `phenotype-${phenotype.id}-${phenotype.idType}`;
  };

  getGroupedAvailablePhenotypeOptions = (): Array<GroupBase<GenomicFeature>> => {
    const groupedOptions: Array<GroupBase<GenomicFeature>> = [];
    const availablePhenotypes = this.props.availablePhenotypes
      .sort((p1, p2) => p1.name.localeCompare(p2.name))
      .filter(
        (phenotype: GenomicFeature) =>
          !this.props.selectedPhenotypes.find((selectedPhenotype: IConfiguredPhenotype) =>
            isSamePhenotypeType(selectedPhenotype, phenotype),
          ),
      );
    const organismAttributes: GenomicFeature[] = availablePhenotypes.filter(
      (option) => option.idType === PHENOTYPE_TYPE.ORG_PROPERTY,
    );
    const sampleAttributes: GenomicFeature[] = availablePhenotypes.filter(
      (option) => option.idType === PHENOTYPE_TYPE.ATTRIBUTE,
    );
    if (organismAttributes.length) {
      groupedOptions.push({
        label: "Organism Attributes",
        options: organismAttributes,
      });
    }
    if (sampleAttributes.length) {
      groupedOptions.push({
        label: "Sample Attributes",
        options: sampleAttributes,
      });
    }
    return groupedOptions;
  };

  render() {
    return (
      <div className={"phenotype-selector"}>
        {this.props.isLoadingPhenotypes && <LoadingSpinner />}
        <SortableList
          id="phenotypes-list"
          onDragEnd={({ active, over }) => {
            const fromIndex = findIndex<IConfiguredPhenotype>(
              (item) => PhenotypeSelector.getPhenotypeId(item) === active.id,
            )(this.props.selectedPhenotypes);
            const toIndex = findIndex<IConfiguredPhenotype>(
              (item) => PhenotypeSelector.getPhenotypeId(item) === over.id,
            )(this.props.selectedPhenotypes);
            this.props.phenotypesUpdated(move(fromIndex, toIndex, this.props.selectedPhenotypes));
          }}
        >
          <SortableContext
            items={this.props.selectedPhenotypes.map(PhenotypeSelector.getPhenotypeId)}
            disabled={this.state.isOpen}
          >
            {this.props.selectedPhenotypes.map((phenotype: IConfiguredPhenotype, index: number) => {
              const label: string = PhenotypeSelector.getPhenotypeLabel(phenotype);

              return (
                <SortableItem
                  key={`phenotype-${phenotype.id}-${phenotype.idType}`}
                  index={index}
                  id={PhenotypeSelector.getPhenotypeId(phenotype)}
                  label={label}
                  onRemove={() => this.removeOption(phenotype)}
                >
                  <PhenotypeSelectorOption
                    phenotype={phenotype}
                    updateOption={this.updateOption}
                    setIsOpen={(isOpen) => this.setState({ isOpen: isOpen })}
                  />
                </SortableItem>
              );
            })}
          </SortableContext>
        </SortableList>

        <div style={{ marginTop: "10px" }}>
          <Select
            isClearable={false}
            isSearchable={true}
            getOptionValue={(option: GenomicFeature) => option.id}
            getOptionLabel={(option: GenomicFeature) => PhenotypeSelector.getPhenotypeLabel(option)}
            isDisabled={false}
            isLoading={this.props.isLoadingPhenotypes}
            options={this.getGroupedAvailablePhenotypeOptions()}
            classNamePrefix={"react-select"}
            placeholder={this.props.dropdownLabel}
            onChange={this.addOption}
            noOptionsMessage={() => "No available phenotype..."}
            controlShouldRenderValue={false}
            onBlurResetsInput={true}
            menuPosition={"absolute"}
            menuPortalTarget={document.body}
            menuShouldScrollIntoView={false}
            styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 9999 }) }}
          />
        </div>
      </div>
    );
  }
}

interface IPhenotypeSelectorOptionProps {
  phenotype: IConfiguredPhenotype;
  updateOption: (phenotype: IConfiguredPhenotype) => void;
  setIsOpen: (isOpen: boolean) => void;
}

const PhenotypeSelectorOption: FC<IPhenotypeSelectorOptionProps> = (props) => {
  const { phenotype, updateOption } = props;
  const label = PhenotypeSelector.getPhenotypeLabel(phenotype);

  return (
    <>
      <PopoverController
        withForm
        actionWidget={(controllerProps) => (
          <TriggerButton
            {...controllerProps}
            onPress={() => {
              controllerProps.onPress();
              props.setIsOpen(true);
            }}
            size={TriggerButtonSize.Small}
            tooltip={"Configure some properties of phenotype"}
            icon={"edit"}
          >
            <VisuallyHidden>Configure {label}</VisuallyHidden>
          </TriggerButton>
        )}
      >
        {(controllerProps) =>
          phenotype.valueType === ORG_PROPERTY_VALUE_TYPE.NUMERIC ? (
            <ColorSchemeConfigurationDialogForm
              {...controllerProps}
              phenotype={phenotype}
              onApply={(phenotype) => {
                updateOption(phenotype);
                controllerProps.closeFn();
              }}
              closeFn={() => {
                controllerProps.closeFn();
                props.setIsOpen(false);
              }}
              initialValues={{
                scale: phenotype.colorScheme,
                min: String(phenotype.minValue),
                max: String(phenotype.maxValue),
                log: phenotype.logScale,
              }}
            />
          ) : (
            <PhenotypeTermConfigDialogForm
              {...controllerProps}
              phenotype={phenotype}
              onApply={(phenotype) => {
                updateOption(phenotype);
                controllerProps.closeFn();
              }}
              closeFn={() => {
                controllerProps.closeFn();
                props.setIsOpen(false);
              }}
            />
          )
        }
      </PopoverController>

      <span className={style.label}>
        {label}
        <div className={style.sub_label}>{`${phenotype.description ? phenotype.description : ""}`}</div>
      </span>
    </>
  );
};

export default PhenotypeSelector;
